/**
 * 素材內容檢查域型別(R209;對齊後端確認清單 2026-08-05 C 節「報告資料契約」)。
 * -----------------------------------------------------------------------------
 * 本檔是 **domain 型別**:wire DTO + mapper 在
 * [check.mappers.ts](./check.mappers.ts)(報告主體 camelCase、job doc snake,兩套
 * 別混);fixture 實作直接產同一個形狀 → 兩實作在 domain 型別會合,UI 不分來源。
 * 端點與 wire 欄位表 = `creative-test-v2/docs/88-asset-check-api-reference.md`
 * (BE `app/asset_check/` P1 已交付並部署 dev,2026-08-06)。
 *
 * 契約要點(後端清單 C 節 + 三輪覆核):
 *   - badge 推導:`fail>0→fail(n=fail,w=warn)`;否則 `warn(n=warn,u=unk)`;否則
 *     `unk(u)`;否則 `pass`。**fail 的次鍵是 `w`、warn 的次鍵是 `u`**(check-data.js:379-380)。
 *   - `err`(檢查失敗)無報告、帶 `reason` 字串(C:104-106)。
 *   - `summary` entry = `{k,n,t}` 三鍵 — `t` 單位字串由後端常數表出,FE 不自拼(C 詳解 #3)。
 *   - `verdictTitle` = string 必填、`verdictLines` = string[]、`verdictDo` = 選填(C:72 三輪更正)。
 *   - `usedIn` 契約定 array[string](C:91 三輪更正;mock 是頓號串接字串)。
 *   - `summary.pass` === Σ`cats[].pass`(後端單向推導,FE 不得自行加總覆寫;C:107-108)。
 */

// ─── 燈號與 badge ───────────────────────────────────────────────────────────

/** 四燈號(字樣與色固定;check-data.js CHK_LAMP)。 */
export type CheckLamp = 'fail' | 'warn' | 'pass' | 'unk';

/**
 * 清單「檢查結果」badge(H-8 `check_badge` + C:103-106 推導規則)。
 * 「未檢查」= 整包缺席(報告 404 / listGroupItems 無此欄),不是第六個 `s` 值。
 */
export interface CheckBadge {
  s: CheckLamp | 'err';
  /** fail / warn 的主計數。unk 態**沒有** n(計數在 `u`;check-data.js:381)。 */
  n?: number;
  /** fail 態的次鍵:另有幾處建議調整。 */
  w?: number;
  /** warn / unk 態的次鍵:幾項需人工確認。 */
  u?: number;
  /** 檢查日期(`YYYY-MM-DD HH:mm`)。 */
  d: string;
  /** err 態的失敗原因(人話;C:104「漏 reason 則清單頁點檢查失敗無內容可顯」)。 */
  reason?: string;
  /**
   * 已套用修改建議的則數(D3 08-07 訂版 C-3;BE `CheckBadge.fixed`)。
   *
   * ⚠️ **badge 的扣抵由後端算,前端一個字都不要自己減**:BE 出的就是
   * `derive(summary − applied)` 的結果(`service.py:1065`),`listGroupItems` 的
   * `check_badge` 已經是扣抵後的值。前端自算會變成「清單 badge 與報告數字兩套帳」
   * —— 後端清單 C-3 把這條列成 blocker 級裁定。
   *
   * 這欄只用來組 hover 文案(全修完的 pass:「已套用 N 則修改建議,目前沒有發現
   * 問題。」)。扣抵只算 fail/warn —— unk 沒有 fix、不可套用。
   */
  fixed?: number;
}

// ─── 檢查目標識別 ───────────────────────────────────────────────────────────

/**
 * 檢查目標 — 素材庫 item 複合鍵(H 節首條:無全域 asset id,路由走 A 案)。
 */
export interface CheckTarget {
  libId: string;
  itemId: string;
  /**
   * 多角度群組素材(產品卡 2×2)的分組鍵 —— **FE 本機顯示用,絕不上 wire**。
   *
   * ⚠️ docs/88 §1 的 `targets[]` **只收 `lib_id` / `item_id`**,且 BE `CheckTarget`
   * 是 `extra="forbid"` → 夾帶 `group_key`(R211 幹過一次)= 整批吃 FastAPI 裸 422
   * (外殼是 `{detail:[...]}` 不是 BaseResponse,errorMap 連業務碼都拿不到),
   * 一件都不會跑。剝除點在
   * [httpCheckService](../services/http/httpCheckService.ts) 的 `targetBody()`,
   * 回歸測試釘在 `httpCheckService.test.ts`(「targets 只有 lib_id/item_id」)。
   */
  groupKey?: string;
}

/** 複合鍵字串(store map key / DOM key 用;新程式碼傳遞一律用 CheckTarget 物件)。 */
export function checkTargetKey(t: Pick<CheckTarget, 'libId' | 'itemId'>): string {
  return `${t.libId}:${t.itemId}`;
}

// ─── 報告主體(三型共用欄)──────────────────────────────────────────────────

export type CheckCatKey = 'G' | 'A' | 'B' | 'C' | 'D';

/** G 類的三小組統計(長度與數量/文字寫法/內容與法規)。 */
export interface CheckCatGroup {
  n: string;
  counts: Partial<Record<'fail' | 'warn' | 'unk', number>>;
  pass: number;
  total: number;
}

export interface CheckCat {
  k: CheckCatKey;
  pass: number;
  total: number;
  sev: CheckLamp;
  counts: Partial<Record<'fail' | 'warn' | 'unk', number>>;
  top: string;
  groups?: CheckCatGroup[];
  note?: string;
}

/** 逐項結果(影/圖用 first/share/pos;文案用 fmt/ref)。 */
export interface CheckItem {
  /**
   * registry 穩定 id(docs/88 §8.2:**跨報告比對一律用這個,不要比 `n` 字串**
   * —— 項目名措辭一漂,靠字串比就會對不上)。R212 起隨 wire 帶入。
   */
  itemId?: string;
  cat: CheckCatKey;
  /** G 類小組名。 */
  g?: string;
  s: CheckLamp;
  /** 項目名(= registry 顯示字)。 */
  n: string;
  /** 性質(如「必備」)。 */
  kind?: string;
  /** 問題描述。 */
  r: string;
  /** 版位名或「全部」(文案 items 皆有;C:75 缺欄則版位 tab 內容全空)。 */
  fmt?: string;
  /** 影片:第一次出現時間碼(`0:02` / `—`)。 */
  first?: string;
  /** 影片:佔全片比例(`46%` / `—`)。 */
  share?: string;
  /** 圖片:畫面位置。 */
  pos?: string;
  /** 文案:指到哪幾則(`短標題 3、CTA 文字 5`)。 */
  ref?: string;
  /** 判定理由(docs/88 §8.2 `why`;R212 補接)。 */
  why?: string;
  /** 修正 Prompt(可直接貼回描述欄;pass/unk 不給 — C:116)。 */
  prompt?: string;
  /**
   * 位置(六欄版型「檢查項目」欄的 chip)—— 影片是時間碼「0:04–0:06 結尾」、
   * 文案是「短標題 3」、圖片是畫面位置;非通過項才有(docs/88 §8.2)。
   *
   * ⚠️ **這是 wire 欄位**,不是拿 `first`/`share`/`pos` 拼出來的:文案與圖片項目
   * 根本沒有那三個影片欄,只靠拼字串等於整條 chip 不出。
   */
  where?: string;
  /**
   * 「建議怎麼做」(六欄版型第 4 欄主行;非通過項才有)。R213 起 **wire 直給** ——
   * 在此之前只在 `advice[]` 裡,要靠中文標題字串對回 item(措辭一漂就整欄 fallback
   * 成「已檢查,需要你協助確認。」;docs/88 §8.2 明令比對用 `item_id`)。
   */
  how?: string;
  /** 預期效果(第 4 欄副行:改了會怎樣;非通過項才有 — docs/88 §8.2)。 */
  eff?: string;
}

export interface CheckAdvice {
  s: CheckLamp;
  t: string;
  /** registry 穩定 id(BE 2026-08-07 起附;舊報告缺鍵)— 與 items[].itemId 比對用。 */
  itemId?: string;
  problem: string;
  where: string;
  how: string;
  eff: string;
  cta: string;
}

/** summary entry — `t` 是單位字串(「處可能退件」),由後端常數表出。 */
export interface CheckSummaryEntry {
  k: CheckLamp;
  n: number;
  t: string;
}

/** 影片限定 ABCD 符合率。 */
export interface CheckRate {
  pct: number;
  pass: number;
  total: number;
  label: string;
  hint: string;
  note: string;
}

/** 下一步動作字樣(**版面本輪不渲染** — 08-05 版型死碼待設計拍板;型別保留鎖契約)。 */
export interface CheckNext {
  note: string;
  main: string;
  second: string;
  secondHint: string;
  third: string;
}

export interface CheckFoot {
  h: string;
  p: string;
}

/** 影片無語音 5 項合併提示(合併卡本體**本輪不渲染** — 版型 B 未掛,待設計拍板;
 * 但其修正 Prompt 進「修正 Prompt」彙整卡(check-detail.js:175 實渲染)。 */
export interface CheckMerged {
  s: CheckLamp;
  title: string;
  body: string;
  how: string;
  /** 合併提示的修正 Prompt(= mock MPROMPT;進彙整卡)。 */
  prompt?: string;
}

export interface CheckNa {
  label: string;
  names: string[];
}

/** 影片風格特徵段(**本輪不渲染** — 版型 B 未掛,待設計拍板;型別保留)。 */
export interface CheckStyle {
  t: string;
  n: string;
}

// ─── 文案型專屬 ─────────────────────────────────────────────────────────────

export interface CopyRow {
  /** 原文案。 */
  x: string;
  s: CheckLamp;
  /** fail/warn:問題短句。 */
  issue?: string;
  /** unk:為什麼判不了。 */
  why?: string;
  /** 修改建議(LLM 產出;fail/warn 給。R334 前文案叫「修改範例」)。 */
  fix?: string;
  /**
   * 原文案中要畫記的字串(底線 mark;fail 紅 / warn 黃)。
   * BE 契約未明列 — mock 是 demo 專屬 MARK 表;已記 docs/54 請 BE 隨報告回。
   */
  marks?: string[];
}

export interface CopyField {
  k: string;
  t: string;
  /** 加權字元上限(全形 2 / 半形 1;SSOT = BE `ad_char_len`)。 */
  lim: number;
  min: number;
  max: number;
  note?: string;
  /**
   * 多行欄位旗標(= D3 `TEXTSPEC[].ml`;長標題/描述為 true)。燈箱「>3 則排兩欄」
   * 的判定**必須以本旗標為據,不可用字數硬編**(確認清單 D 節三輪:欄位上限一調
   * 就漂移)。BE 契約未列 → docs/54 assets.13 已請補;缺值時呼叫端 fallback。
   */
  ml?: boolean;
  rows: CopyRow[];
}

/**
 * 版位定義 —— **wire `placements[]`(docs/88 §8.2 `PlacementSpec`)的 domain 形狀**;
 * BE 真理源 = `FORMAT_FIELD_BOUNDS`(E-4 拍板:另立 per-format cap 表,勿與生成側
 * `COUNT_BOUND` 混用)。BE 沒給時才由
 * [copyPlacements](../features/assets/check/copyPlacements.ts) 的本機表依 `usedIn` 派生。
 */
export interface CheckPlacement {
  /** 格式 id(wire `id`,如 `RESPONSIVE_SEARCH_AD`)。 */
  k: string;
  /** 顯示名(wire `label`;FE 對已知格式改用自己的 i18n)。 */
  t: string;
  en?: string;
  /** 格式名(組句用,如「回應式多媒體廣告」)。 */
  fmt?: string;
  /** 這個版位用到的欄位 key(wire `fields`)。 */
  fields: string[];
  /**
   * per-欄位**則數上限**(wire `caps`;如多媒體聯播網 short_title→5)。
   * 沒列的欄位才退 `CopyField.max` —— 而 `max` 是**跨格式聯集**(BE 對多格式報告取
   * `max(his)`),兩個分頁會顯示同一個放寬過的數字,所以有 `cap` 一律以 `cap` 為準。
   */
  cap: Record<string, number>;
  /** per-欄位**則數下限**(wire `mins`;0 = 選填)。同理優先於聯集的 `CopyField.min`。 */
  min?: Record<string, number>;
}

// ─── 報告 ───────────────────────────────────────────────────────────────────

export type CheckAssetType = 'video' | 'image' | 'text';

/** 報告邊界態(由報告回應欄位驅動;FE 不自算 stale — BE B-57 P1 不做 stale 偵測)。 */
export type CheckReportState = 'ok' | 'stale' | 'lowconf';

export interface CheckReport {
  id: string;
  name: string;
  type: CheckAssetType;
  /**
   * 規格**顯示字串**(方向先講、秒數中文;CIS v1.5 寫法)。
   * ⚠️ docs/88 §8.1:`spec` **只用於顯示** —— 要算長寬比/秒數請用下面四個結構化欄,
   * 別 regex 解這個字串(`1.91:1` 會被 `/(\d+):(\d+)/` 解成 91:1 = 1px 細線)。
   */
  spec: string;
  /** 文案限定:則數明細(C 詳解 #2)。 */
  specSub?: string;
  /** 像素寬(圖片 / 影片;wire `width`)。缺 = BE 沒解出來,顯示層走「—」。 */
  width?: number;
  /** 像素高(圖片 / 影片;wire `height`)。 */
  height?: number;
  /** 影片長度秒數(wire `durationSec`;圖片/文案無)。 */
  durationSec?: number;
  /** 檔案大小 MB(圖片 / 影片;wire `sizeMb`)。 */
  sizeMb?: number;
  /** 圖片限定:構圖描述段(C 詳解 #2)。 */
  compo?: string;
  srcType: string;
  srcName: string;
  made: string;
  checked: string;
  /** 上傳廣告時間(null = 尚未上傳;C 詳解 #1)。 */
  uploaded: string | null;
  standard: string;
  sev: CheckLamp;
  verdictTitle: string;
  verdictLines: string[];
  verdictDo?: string;
  summary: CheckSummaryEntry[];
  cats: CheckCat[];
  items: CheckItem[];
  advice: CheckAdvice[];
  merged?: CheckMerged;
  na?: CheckNa[];
  style?: CheckStyle;
  rate?: CheckRate;
  next: CheckNext;
  foot: CheckFoot[];
  /** 通過項名單(registry 全清單扣掉非 pass;C:109)。 */
  passNames: string[];
  /** 文案限定。 */
  fields?: CopyField[];
  rowsNote?: string;
  /** 整組判定項名單(E-3 三輪裁定聯集 5 項;stale 句由此插值,FE 不硬編)。 */
  groupItems?: string[];
  /**
   * 這組文案投放的**格式 id** 清單(`RESPONSIVE_SEARCH_AD` / `PERFORMANCE_MAX` /
   * `RESPONSIVE_DISPLAY_AD` / `DEMAND_GEN`;docs/88 §8.1)。顯示名 FE 自行映射。
   */
  usedIn?: string[];
  /**
   * 版位定義表(fields / cap / min;顯示層據此切 tab)—— **wire 欄位**
   * (docs/88 §8.1「別在前端寫死格式↔欄位對照」;BE `_WIRE_FIELDS` 白名單含
   * `placements`,由 `build_placements(used_in)` 依 `FORMAT_FIELD_BOUNDS` 產出)。
   *
   * 缺席時(舊報告 / 降級純文字)才由
   * [copyPlacements](../features/assets/check/copyPlacements.ts) 的本機表依 `usedIn`
   * 派生 —— 那份表只有「哪個格式用哪些欄位」,**沒有 per-format 則數上限**。
   */
  placements?: CheckPlacement[];
  /** 報告 id(拿去 copy-apply / group-items 指定同一份;wire `report_id`)。 */
  reportId?: string;
  /** 中文型別字(「文案」/「圖片」/「影片」;wire `typeLabel`)。 */
  typeLabel?: string;
  /** 結構信心不足 → 顯示 lowconf 橫幅(wire `lowConf`)。 */
  lowConf?: boolean;
  /**
   * 有待重檢的整組判定(wire `groupStale`;**由 BE 給,FE 不自算**)。
   * true → 顯 amber 橫幅 +「重新檢查這 N 項」;跑完 BE 清旗標,再按會 409。
   */
  groupStale?: boolean;
  /** 規格版本(回報問題時附上;wire `spec_version`)。 */
  specVersion?: string;
  /** 邊界態(預設 'ok')。 */
  state?: CheckReportState;
  /**
   * 文案套用版(J-2 拍板:存報告 doc `applied_texts`,原文不動;key = `${fieldKey}:${rowIndex}`)。
   * 上傳廣告時用套用版 — 燈箱 liveText 也讀這裡。
   */
  appliedTexts?: Record<string, string>;
  /**
   * R292.20(BE `dev-update-v21-3`)— 本輪檢查**含已套用修正**,值 = 來源報告 id。
   * 缺席 / null = 本輪沒有帶入任何套用稿(舊 BE 也是缺席)。
   *
   * 為什麼要顯示:複檢從這批起會自動帶入最新報告的已套用修正再判(不再讀原文空轉)
   * → 使用者看到「絕對化用語」轉 pass 時,要知道那是**因為套用生效**,不是判定飄。
   */
  appliedFromReportId?: string;
  /**
   * 套用稿因素材後來被編輯、row key 對不上而**被丟棄**的條數。
   * `> 0` 要講出來 —— 使用者以為套過的修正還在,實際上那幾條沒帶進本輪。
   * 缺席 = 0(舊 BE 沒有這一欄)。
   */
  appliedDropped?: number;
  /** 報告引擎版本(I-8 #2:schema 凍結追溯)。 */
  engineVersion?: string;
  /**
   * R424.1(BE docs/137 §5)— 這份報告是用哪個語系產生的(`zh-TW` / `en` / `ja`;舊報告 = `zh-TW`)。
   * 報告裡給人看的文字 BE 已依它產出,FE **不翻譯**;它 ≠ 介面語系時報告頂部提示「重新檢查可換語言」
   * (換語言 = 重檢扣 1 點,owner 拍板不自動)。值域開放:認不得的值原樣保留。
   * **選填**:http mapper 一定寫(缺欄補 `zh-TW`);fixture / 測試資料沒有 → 讀的地方一律 `?? 'zh-TW'`
   * (必填的話 fixture 會在 tsc 範圍外安靜地缺這欄,R350.7)。
   */
  locale?: string;
}

// ─── 批次檢查 ───────────────────────────────────────────────────────────────

/**
 * 批次狀態(顯示態;wire 的 per-item StepStatus 映射在 service/listener 層 —
 * 後端清單 B 節三輪:per-item 沿用 `StepStatus`,別把 mock 詞彙定成 wire 型別)。
 */
/** 四燈號計數(wire `job.counts`;**已完成件**的累計 — 完成 toast 分桶用)。 */
export interface CheckLampCounts {
  fail: number;
  warn: number;
  unk: number;
  pass: number;
}

/** 單件預估秒數的 fallback(docs/88 §2.1 預設值;wire 有給就以 wire 為準)。 */
export const DEFAULT_ETA_SECONDS_PER_ITEM = 33;

export interface CheckBatchStatus {
  batchId: string;
  state: 'run' | 'done' | 'fail' | 'cancelled';
  total: number;
  /** **成功**件數。⚠️ 與 `failed` 分開計(docs/88 §2.1)— 不可相減。 */
  done: number;
  failed: number;
  /** 已取消件數(cancel 後 BE 逐件退點)。 */
  cancelled?: number;
  /** 已完成件的燈號累計(缺值全 0)。 */
  counts?: CheckLampCounts;
  /** 目前素材的子步驟(接收素材→看內容→對照標準→產出建議)。 */
  step: 0 | 1 | 2 | 3;
  /** 檢查中那筆的複合鍵(其後的顯「等待中」)。 */
  currentKey: string | null;
  /** 送檢順序(複合鍵;決定 run/wait)。 */
  order: string[];
  /** 完成一筆寫回一筆(複合鍵 → badge)。 */
  results: Record<string, CheckBadge>;
  /** 複合鍵 → `report_id`(SUCCEEDED 後才有;開報告頁時免再查一次最新)。 */
  reportIds?: Record<string, string>;
  /** 單件預估秒數(wire `eta_seconds_per_item`;倒數用)。 */
  etaSecondsPerItem?: number;
  /** 四個子步驟的顯示字串(wire `stages`;缺則 UI 用自己的 i18n)。 */
  stages?: string[];
  startedAt: number;
  /**
   * BE 算好的 Firestore job doc path(`firestore_paths.job`)。
   * 有值 → `useCheckBatchListener` 走 `useFirestoreSubscription` 訂閱推播
   * (repo listener-only 慣例);fixture 佔位期無此欄,走 service 的 timer 推送。
   */
  firestorePath?: string;
  /**
   * items 子集合 path(`firestore_paths.steps_collection`)。
   * ⚠️ **job doc 沒有 items**(docs/88 §2.3)→ 逐件 badge 只能從這一路來;
   * 少訂這路 = 進度動但一筆結果都不會寫回清單。
   */
  itemsPath?: string;
  /**
   * listener 拿不到權限(rules 未部署 → `permission-denied`)時設 true:
   * UI 改顯「手動更新進度」出口。**不得退成輪詢**(docs/88 §2.3 明禁)。
   */
  listenerBlocked?: boolean;
}

/**
 * 進度百分比 = (**settled**×4＋step)/(total×4),`settled = done + failed + cancelled`
 * (docs/88 §2.3 逐字給的公式)。
 *
 * ⚠️ **分子一定要含 failed / cancelled**:`done` 只計成功件,而每件收斂時 BE 把
 * `current_stage` 歸零 —— 只用 `done` 的話,任一件失敗的當下進度條會**往回跳**
 * (12 件跑到第 3 件失敗:23% → 17%),而且整批跑完也停在 100% 以下。
 *
 * 終態(done/fail/cancelled)仍直接回 100 —— 那是收尾,不是拿來遮上面那條 bug 的。
 */
export function checkBatchPct(
  b: Pick<CheckBatchStatus, 'state' | 'done' | 'step' | 'total'> &
    Partial<Pick<CheckBatchStatus, 'failed' | 'cancelled'>>,
): number {
  if (b.state === 'done' || b.state === 'fail' || b.state === 'cancelled') return 100;
  if (b.total <= 0) return 0;
  const settled = b.done + (b.failed ?? 0) + (b.cancelled ?? 0);
  return Math.round(((settled * CHECK_STAGE_COUNT + b.step) / (b.total * CHECK_STAGE_COUNT)) * 100);
}

/**
 * 每件素材的子步驟數(接收素材→看內容→對照標準→產出建議)。
 *
 * **BE 保證恆 4**(待答清單 B-6:`PROGRESS_STAGES` 是後端常數 tuple,`current_stage`
 * 0–3 就是索引),契約鎖 `asset_check.progress_stage_count` 會在 BE 改階數時變紅。
 * 進度公式的分母與 `check.mappers` 的 `STAGE_MAX` 都由這裡出,別再各寫一個 4。
 */
export const CHECK_STAGE_COUNT = 4;

/**
 * 一批送檢上限(`POST /asset_check/jobs`;docs/87 §1「1..100」)。
 * == BE `CHECK_JOB_MAX_TARGETS`,超過是 FastAPI 422(拿不到業務碼)。
 * 契約鎖 `asset_check.job_max_targets` 守著。
 */
export const CHECK_BATCH_MAX = 100;
